import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'

export default function MobileHome() {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const navigate = useNavigate()

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const resizeCanvas = () => {
      canvas.width = window.innerWidth
      canvas.height = window.innerHeight
    }
    resizeCanvas()
    window.addEventListener('resize', resizeCanvas)

    // Linha de ECG animada
    let ecgLinePos = 0
    const ecgPoints: number[] = []
    const drawECGLine = () => {
      // Gerando pontos de ECG se não existirem
      if (ecgPoints.length === 0) {
        const segmentWidth = canvas.width / 5
        let x = 0
        
        while (x < canvas.width) {
          // Linha base
          for (let i = 0; i < segmentWidth * 0.4; i++) {
            ecgPoints.push(canvas.height / 2)
            x++
          }
          
          // Pico P (pequena elevação)
          for (let i = 0; i < segmentWidth * 0.1; i++) {
            const height = canvas.height / 2 - Math.sin((i / (segmentWidth * 0.1)) * Math.PI) * 10
            ecgPoints.push(height)
            x++
          }
          
          // Volta à linha base
          for (let i = 0; i < segmentWidth * 0.1; i++) {
            ecgPoints.push(canvas.height / 2)
            x++
          }
          
          // Complexo QRS (grande pico)
          // Q (pequena descida)
          for (let i = 0; i < segmentWidth * 0.05; i++) {
            const height = canvas.height / 2 + Math.sin((i / (segmentWidth * 0.05)) * Math.PI) * 5
            ecgPoints.push(height)
            x++
          }
          
          // R (grande subida)
          for (let i = 0; i < segmentWidth * 0.05; i++) {
            const height = canvas.height / 2 - Math.sin((i / (segmentWidth * 0.05)) * Math.PI) * 40
            ecgPoints.push(height)
            x++
          }
          
          // S (descida)
          for (let i = 0; i < segmentWidth * 0.05; i++) {
            const height = canvas.height / 2 + Math.sin((i / (segmentWidth * 0.05)) * Math.PI) * 15
            ecgPoints.push(height)
            x++
          }
          
          // Volta à linha base
          for (let i = 0; i < segmentWidth * 0.1; i++) {
            ecgPoints.push(canvas.height / 2)
            x++
          }
          
          // Onda T (leve elevação)
          for (let i = 0; i < segmentWidth * 0.15; i++) {
            const height = canvas.height / 2 - Math.sin((i / (segmentWidth * 0.15)) * Math.PI) * 15
            ecgPoints.push(height)
            x++
          }
        }
      }
      
      // Desenhar a linha de ECG
      ctx.strokeStyle = 'rgba(37, 99, 235, 0.2)'
      ctx.lineWidth = 1.5
      ctx.beginPath()
      
      // Movendo a linha para simular movimento
      ecgLinePos = (ecgLinePos + 0.7) % ecgPoints.length
      
      for (let x = 0; x < canvas.width; x++) {
        const index = (x + Math.floor(ecgLinePos)) % ecgPoints.length
        const y = ecgPoints[index]
        
        if (x === 0) {
          ctx.moveTo(x, y)
        } else {
          ctx.lineTo(x, y)
        }
      }
      
      ctx.stroke()
    }

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      drawECGLine()
      requestAnimationFrame(animate)
    }

    animate()

    return () => window.removeEventListener('resize', resizeCanvas)
  }, [])

  return (
    <div className="relative min-h-screen overflow-hidden">
      {/* Gradientes animados em camadas */}
      <div className="absolute inset-0">
        {/* Gradiente base */}
        <div className="absolute inset-0 bg-gradient-to-b from-background via-primary/5 to-background" />
        
        {/* Gradiente radial pulsante */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-primary/10 via-transparent to-transparent animate-pulse-slower" />
        
        {/* Gradiente diagonal animado */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-primary/10 animate-gradient-slower" />
        
        {/* Vinheta suave nas bordas */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_transparent_50%,_rgba(0,0,0,0.1)_100%)]" />
      </div>
      
      <canvas
        ref={canvasRef}
        className="absolute inset-0 pointer-events-none"
        style={{ opacity: 0.7 }}
      />

      <div className="relative flex flex-col min-h-screen">
        <div className="flex-grow flex flex-col items-center justify-center px-4 py-8 gap-8">
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="flex flex-col items-center"
          >
            <div className="mb-6 flex flex-col items-center">
              <div className="w-20 h-20 rounded-3xl flex items-center justify-center bg-primary shadow-xl backdrop-blur-sm bg-opacity-90">
                <svg
                  className="w-10 h-10 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M3 12h2l2 -8 2 16 2 -12 2 8 1.5 -3 2 0 1 -2 3.5 0" />
                  <path d="M3 8h18" strokeOpacity="0.3" strokeWidth="1" />
                  <path d="M3 16h18" strokeOpacity="0.3" strokeWidth="1" />
                </svg>
              </div>

              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="flex flex-col items-center gap-1"
              >
                <span className="text-2xl font-semibold">Simples.ECG</span>
                <a
                  href="https://simples.health"
                  target="_blank"
                  rel="noreferrer"
                  className="text-sm text-muted-foreground hover:text-primary transition-colors"
                >
                  por Simples.Health
                </a>
              </motion.div>
            </div>

            {/* Social Proof + Headline */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="space-y-6"
            >
              <div className="flex items-center justify-center gap-2 text-sm font-medium text-primary animate-pulse">
                <span className="inline-block w-2 h-2 rounded-full bg-primary" />
                +2.000 médicos já utilizam
              </div>

              <h1 className="text-3xl font-bold leading-tight">
                <span className="text-primary-300">Interprete ECG com IA</span>{' '}
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#1E3A8A] to-primary">
                  em menos de 15 segundos
                </span>
              </h1>
              
              <p className="text-lg text-text-200">
                Tenha mais segurança nas suas decisões com análises profundas e instantâneas. 
                <strong className="text-primary-300"> 100% gratuito.</strong>
              </p>
            </motion.div>

            {/* Métricas de Confiança */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.6 }}
              className="grid grid-cols-2 gap-3 mt-8"
            >
              <div className="bg-white/80 backdrop-blur-sm shadow-md rounded-xl p-3 text-center">
                <div className="text-xl font-bold text-primary">+10k</div>
                <div className="text-xs text-muted-foreground">Diagnósticos</div>
              </div>
              <div className="bg-white/80 backdrop-blur-sm shadow-md rounded-xl p-3 text-center">
                <div className="text-xl font-bold text-primary">98%</div>
                <div className="text-xs text-muted-foreground">Precisão</div>
              </div>
              <div className="bg-white/80 backdrop-blur-sm shadow-md rounded-xl p-3 text-center">
                <div className="text-xl font-bold text-primary">15s</div>
                <div className="text-xs text-muted-foreground">Tempo médio</div>
              </div>
              <div className="bg-white/80 backdrop-blur-sm shadow-md rounded-xl p-3 text-center">
                <div className="text-xl font-bold text-primary">100%</div>
                <div className="text-xs text-muted-foreground">Gratuito</div>
              </div>
            </motion.div>

            {/* CTA */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.8 }}
              className="w-full mt-8"
            >
              <button 
                onClick={() => navigate('/register')}
                className="w-full relative inline-flex items-center justify-center py-4 px-6 text-lg font-semibold text-white bg-primary rounded-xl overflow-hidden shadow-xl"
              >
                <span className="relative z-10 flex items-center">
                  Começar Agora
                  <span 
                    className="ml-2 transform transition-transform duration-300"
                    aria-hidden="true"
                  >
                    →
                  </span>
                </span>
              </button>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </div>
  )
} 